import { inject, Injectable } from '@angular/core';
import { AuthConfig } from '../../environments/environment';
import {UserInfoService} from "./user-info.service";
import { catchError, map, Observable, of } from 'rxjs';
import { UserInfo } from '../models/user-info.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly accessTokenKey = 'access_token_sms';
    private readonly userInfoService = inject(UserInfoService);

    setAccessToken(token: string) {
        const expires = new Date(Date.now() + 12 * 60 * 60 * 1000).toUTCString();
        document.cookie = `${this.accessTokenKey}=${token}; expires=${expires}; path=/`;
    }

    getAccessToken(): string | null {
        const name = this.accessTokenKey + '=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(';');
        for (let c of ca) {
            c = c.trim();
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return null;
    }

    isAuthenticated(): Observable<boolean> {
        const accessToken = this.getAccessToken();
        if (!accessToken) {
            return of(false);
        }

        return this.userInfoService.getUserInfo(accessToken).pipe(
            map((userInfo: UserInfo) => {
                if (!userInfo?.sub) {
                    return false;
                }

                this.userInfoService.setIdentity(userInfo);
                return true;
            }),
            catchError(() => of(false))
        );
    }

    logout() {
        document.cookie = `${this.accessTokenKey}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        this.userInfoService.clearIdentity();
    }

    getAuthUrl(): string {
        return `${AuthConfig.AMBaseURL}/oauth2/${AuthConfig.realmPath}/authorize?client_id=${AuthConfig.clientId}&state=${AuthConfig.state}&scope=${AuthConfig.scopes}&redirect_uri=${AuthConfig.redirectUri}&response_type=token%20id_token&nonce=${AuthConfig.nonce}`;
    }

    handleRedirect() {
        if (sessionStorage.getItem('redirectHandled')) return;

        const hash = window.location.hash;
        if (hash) {
            const params = new URLSearchParams(hash.substring(1));
            const accessToken = params.get('access_token');
            if (accessToken) {
                this.setAccessToken(accessToken);
                sessionStorage.setItem('redirectHandled', 'true');
                // Remove the hash from the URL before reload
                history.replaceState(null, '', window.location.pathname + window.location.search);
                window.location.reload();
                return;
            }
        }
        window.location.href = this.getAuthUrl();
    }

    handleRefreshRedirect() {
        window.location.href = this.getAuthUrl();
    }
}
